import { toDimension } from '@upright/common/model/data';
import { ImpactCategoryId } from '@upright/common/model/input/impacts/impact-categories';
import {
  CsrdMatterHierarchy,
  SustainabilityMatter,
  SustainabilityMatterId,
} from '@upright/common/model/input/materiality-assessment/types-matters';
import { friendlyDimensionName } from '@upright/common/model/texts/friendly-dimension-name';
import { Dimension, orderedDimensions, orderedImpacts } from '@upright/common/model/types';
import { friendlyShortImpactTitle } from '@upright/common/utils';
import { getMatterById } from '.';

export function getUprightMatters(): SustainabilityMatter[] {
  return [getDimensionBasedMatters(), getUprightImpactCategoryBasedMatters()].flat();
}

export const uprightSustainabilityMatters = getUprightMatters();

function getUprightImpactCategoryBasedMatters(): SustainabilityMatter[] {
  return orderedImpacts.map(impactCategory => {
    const ret: SustainabilityMatter = {
      id: uprightImpactCategoryToMatterId(impactCategory),
      parent: uprightDimensionToMatterId(toDimension(impactCategory)),
      title: friendlyShortImpactTitle(impactCategory),
      hierarchy: CsrdMatterHierarchy.UPRIGHT,
    };
    return ret;
  });
}

function getDimensionBasedMatters(): SustainabilityMatter[] {
  return orderedDimensions.map(d => {
    const ret: SustainabilityMatter = {
      id: uprightDimensionToMatterId(d),
      parent: undefined,
      title: friendlyDimensionName(d),
      hierarchy: CsrdMatterHierarchy.UPRIGHT,
    };
    return ret;
  });
}

function uprightDimensionToMatterId(dimension: Dimension) {
  const matterId = `U-${dimension}`;
  return matterId as SustainabilityMatterId;
}

export function uprightImpactCategoryToMatterId(category: ImpactCategoryId) {
  const matterId = `U-${category}`;
  return matterId as SustainabilityMatterId;
}

// These are covered by ESRS matters, and therefore are not shown separately to customers as "entity-specific matters"
const irrelevantUprightMatters = [
  SustainabilityMatterId.UPRIGHT_EQUALITY,
  SustainabilityMatterId.UPRIGHT_GHG_EMISSIONS,
  SustainabilityMatterId.UPRIGHT_NON_GHG_EMISSIONS,
  SustainabilityMatterId.UPRIGHT_SCARCE_NATURAL_RESOURCES,
  SustainabilityMatterId.UPRIGHT_BIODIVERSITY,
  SustainabilityMatterId.UPRIGHT_WASTE,
  SustainabilityMatterId.UPRIGHT_PHYSICAL_DISEASES,
  SustainabilityMatterId.UPRIGHT_MENTAL_DISEASES,
];

export const relevantUprightMatterIds = uprightSustainabilityMatters
  .filter(matter => !isIrrelevantUprightMatter(matter.id))
  .map(matter => matter.id);

export function isUprightMatter(matterId: SustainabilityMatterId) {
  const matter = getMatterById(matterId);
  return matter.hierarchy === CsrdMatterHierarchy.UPRIGHT;
}

export function isRelevantUprightMatter(matterId: SustainabilityMatterId) {
  return isUprightMatter(matterId) && !isIrrelevantUprightMatter(matterId);
}

export function isIrrelevantUprightMatter(matterId: SustainabilityMatterId) {
  return irrelevantUprightMatters.includes(matterId);
}
